import {
  catalinaIslandHarbor,
  catalinaMap,
  diverBluefinBelow,
  diverWsbUnderwater,
  diverYellowtailAscending,
  groupWsbCatch,
  heroBluefinDeck,
  heroWsbKelp,
  sanClementeMap,
} from '../../assets/images/hero/imageAssets';

export interface CharterDestination {
  id: string;
  slug: string;
  title: string;
  tagline: string;
  category: 'spearfishing' | 'sportfishing' | 'private';
  heroImage: string;
  mapImage?: string;
  briefDescription: string;
  fullDescription: string[];
  species: Array<{ name: string; season: string }>;
  tripOptions: Array<{ name: string; duration: string; price: string }>;
  uniqueDetails: string[];
  included: string[];
  toBring: string[];
  logistics: {
    departureTime: string;
    departureLocation: string;
    maxGuests: number;
    depositRequired: string;
    cancellationPolicy: string;
  };
  seo: {
    title: string;
    description: string;
    h1: string;
    keywords: string[];
  };
}

// ─── Shared defaults ────────────────────────────────────────────────────

const DEFAULT_LOGISTICS: CharterDestination['logistics'] = {
  departureTime: '5:30 AM (adjusted based on conditions)',
  departureLocation: '224 Whalers Walk, San Pedro, CA 90731',
  maxGuests: 6,
  depositRequired: '50% deposit to confirm',
  cancellationPolicy:
    '14+ days: full refund. Under 14 days: deposit forfeited. Weather cancellations: full reschedule or refund.',
};

const STANDARD_INCLUDED: string[] = [
  'Captain and crew',
  'All spearfishing gear (spearguns, wetsuits, masks, fins, weight belts)',
  'Safety equipment (O2, first aid, float lines)',
  'Fish handling — bled, iced, cleaned',
  'Ice and fish bags',
  'Fuel',
];

const STANDARD_TO_BRING: string[] = [
  'Valid California fishing license with ocean enhancement stamp',
  'Sunscreen and hat',
  'Food and water for the trip',
  'Towel and change of clothes',
  'Camera (waterproof recommended)',
  'Personal gear if preferred (not required)',
];

// ─── Destination data ───────────────────────────────────────────────────

export const charterDestinations: CharterDestination[] = [
  // 1. Catalina Island
  {
    id: 'catalina',
    slug: 'catalina',
    title: 'Catalina Island',
    tagline: 'Crystal-clear kelp forests, world-class White Seabass, and Yellowtail that run hard all summer',
    category: 'spearfishing',
    heroImage: catalinaIslandHarbor,
    mapImage: catalinaMap,
    briefDescription:
      'Catalina Island offers some of the best spearfishing in Southern California. Crystal-clear visibility, expansive kelp forests, and a healthy population of White Seabass, Yellowtail, and Calico Bass make this a must-dive destination.',
    fullDescription: [
      'Catalina Island sits 22 miles off the coast of San Pedro and is one of the premier spearfishing destinations in all of Southern California. The island features miles of pristine kelp forests, rocky reef structure, and blue-water drop-offs that hold a wide variety of game fish year-round.',
      'From May through July, White Seabass move into the kelp beds around Catalina in force, making it the prime window for landing a trophy fish. Yellowtail push through from June to September, and Calico Bass and Sheephead are reliable targets any time of year.',
      'Captain Christos knows every reef, ledge, and kelp line around the island. Whether you are an experienced diver looking to push your limits or a newer spearo looking to learn the craft, Catalina delivers consistently.',
    ],
    species: [
      { name: 'White Seabass', season: 'May - Jul' },
      { name: 'Yellowtail', season: 'Jun - Sept' },
      { name: 'Calico Bass', season: 'Year-round' },
      { name: 'Sheephead', season: 'Year-round' },
      { name: 'Halibut', season: 'Apr - Sept' },
      { name: 'Lingcod', season: 'Year-round' },
    ],
    tripOptions: [
      { name: 'Full-Day Charter', duration: '8 hours', price: 'From $2,999' },
      { name: 'Private Charter', duration: 'Custom', price: 'Contact for pricing' },
    ],
    uniqueDetails: [
      '22 miles offshore — shorter run, more time in the water',
      'Some of the clearest water in Southern California',
      'Expansive kelp forests with reliable White Seabass',
      'Protected leeward coves for calm diving conditions',
      'Yellowtail run hard along the frontside from June through September',
      'Rocky reef and boulder structure holding Calico and Sheephead year-round',
      'Multiple dive sites in a single trip — backside, frontside, blue water',
      'Ideal destination for both experienced and newer spearfishers',
    ],
    included: STANDARD_INCLUDED,
    toBring: STANDARD_TO_BRING,
    logistics: DEFAULT_LOGISTICS,
    seo: {
      title:
        'Spearfishing Catalina Island — Charter from San Pedro | Fussaki',
      description:
        'Premium spearfishing charters to Catalina Island from San Pedro, CA. Target White Seabass, Yellowtail, Calico Bass in crystal-clear kelp forests. Book with Captain Christos.',
      h1: 'Catalina Island Spearfishing Charters',
      keywords: [
        'Catalina Island spearfishing',
        'spearfishing charter Catalina',
        'White Seabass Catalina',
        'San Pedro fishing charter',
        'Catalina kelp forest diving',
      ],
    },
  },

  // 2. San Clemente Island
  {
    id: 'san-clemente-island',
    slug: 'san-clemente-island',
    title: 'San Clemente Island',
    tagline: 'Deep water, bigger fish, less pressure',
    category: 'spearfishing',
    heroImage: diverYellowtailAscending,
    mapImage: sanClementeMap,
    briefDescription:
      'San Clemente Island sits 55 miles offshore and offers some of the biggest fish in Southern California. Less boat traffic, cleaner water, and access to Yellowtail, Bluefin Tuna, and White Seabass that rarely see divers.',
    fullDescription: [
      'San Clemente Island is the serious spearo\'s destination. At 55 miles offshore, it takes commitment to get there, but the payoff is worth every minute of the run. The island sees far less pressure than Catalina, and the fish reflect it — bigger Yellowtail, healthier White Seabass populations, and access to blue-water pelagics like Bluefin Tuna and Dorado.',
      'The island\'s west side features dramatic drop-offs, pinnacles, and kelp forests that hold fish year-round. During the summer months, warm currents push in and bring with them schools of Yellowtail and the occasional Bluefin. White Seabass patrol the kelp edges from May through July.',
      'These are full-day expeditions, typically 10-12 hours. Captain Christos runs this trip when conditions align — clean water, favorable weather, and fish reports that warrant the longer run.',
    ],
    species: [
      { name: 'Yellowtail', season: 'Jul - Sept' },
      { name: 'White Seabass', season: 'May - Jul' },
      { name: 'Bluefin Tuna', season: 'Aug - Nov' },
      { name: 'Dorado', season: 'Jul - Oct' },
      { name: 'Calico Bass', season: 'Year-round' },
      { name: 'Sheephead', season: 'Year-round' },
    ],
    tripOptions: [
      {
        name: 'Full-Day Expedition',
        duration: '10-12 hours',
        price: 'From $3,499',
      },
      {
        name: 'Private Charter',
        duration: 'Custom',
        price: 'Contact for pricing',
      },
    ],
    uniqueDetails: [
      '55 miles offshore — less pressure, bigger fish',
      'Access to blue-water pelagics including Bluefin Tuna',
      'Dramatic drop-offs and pinnacle structure',
      'Conditions-driven scheduling for optimal results',
      'Key spots: Castle Rock, Nine Fathom, Eel Point, Mackerel Bank',
      'Some of the clearest water in Southern California',
      'Trophy-class White Seabass and Yellowtail',
      'Military-controlled island — minimal fishing pressure year-round',
    ],
    included: STANDARD_INCLUDED,
    toBring: STANDARD_TO_BRING,
    logistics: DEFAULT_LOGISTICS,
    seo: {
      title:
        'Spearfishing San Clemente Island — Deep Water Charter | Fussaki',
      description:
        'San Clemente Island spearfishing charters from San Pedro. 55 miles offshore — bigger fish, less pressure. Yellowtail, Bluefin Tuna, White Seabass. Full-day expeditions.',
      h1: 'San Clemente Island Spearfishing Charters',
      keywords: [
        'San Clemente Island spearfishing',
        'spearfishing charter San Clemente',
        'Bluefin Tuna spearfishing',
        'deep water spearfishing California',
        'offshore spearfishing charter',
      ],
    },
  },

  // 3. Open Blue Water
  {
    id: 'open-blue-water',
    slug: 'open-blue-water',
    title: 'Open Blue Water',
    tagline: 'Offshore pelagics — Bluefin Tuna, Yellowfin, Dorado',
    category: 'spearfishing',
    heroImage: diverBluefinBelow,
    briefDescription:
      'Blue water spearfishing is the pinnacle of the sport. These are conditions-driven trips targeting Bluefin Tuna, Yellowfin Tuna, and Dorado in open ocean. Captain Christos uses satellite data, water temperature charts, and years of experience to put you on fish.',
    fullDescription: [
      'Blue water spearfishing is as real as it gets. No reef to hide behind, no kelp to anchor to — just you, the open ocean, and pelagic predators. These trips target Bluefin Tuna, Yellowfin Tuna, Dorado, and Yellowtail in offshore waters where the fish are big and the conditions demand respect.',
      'Captain Christos monitors satellite imagery, sea surface temperatures, chlorophyll charts, and local reports to determine when and where to run these trips. When conditions align, the results can be legendary — Bluefin Tuna over 100 pounds, schools of Dorado stacking under kelp paddies, and Yellowtail cruising the current lines.',
      'These are not scheduled trips. Blue water expeditions run when the ocean says it is time. If you want to be on the list when conditions fire, reach out and Captain Christos will keep you in the loop.',
    ],
    species: [
      { name: 'Bluefin Tuna', season: 'Aug - Nov' },
      { name: 'Yellowfin Tuna', season: 'Seasonal' },
      { name: 'Dorado', season: 'Jul - Oct' },
      { name: 'Yellowtail', season: 'Jun - Nov' },
    ],
    tripOptions: [
      {
        name: 'Full-Day Expedition',
        duration: '8-12 hours',
        price: 'From $3,499',
      },
    ],
    uniqueDetails: [
      'Conditions-driven — trips run when the ocean is right',
      'Satellite-guided targeting using SST and chlorophyll data',
      'Big-game pelagics including Bluefin Tuna over 100 lbs',
      'Expert captain with deep blue water experience',
    ],
    included: STANDARD_INCLUDED,
    toBring: STANDARD_TO_BRING,
    logistics: DEFAULT_LOGISTICS,
    seo: {
      title:
        'Blue Water Spearfishing California — Bluefin Tuna Charter | Fussaki',
      description:
        'Offshore blue water spearfishing for Bluefin Tuna, Yellowfin, and Dorado. Conditions-driven trips from San Pedro, CA. Expert captain, satellite-guided targeting.',
      h1: 'Blue Water Spearfishing Charters',
      keywords: [
        'blue water spearfishing',
        'Bluefin Tuna spearfishing California',
        'offshore spearfishing charter',
        'pelagic spearfishing',
        'Yellowfin Tuna spearfishing',
      ],
    },
  },

  // 4. Kelp Forests
  {
    id: 'kelp-forests',
    slug: 'kelp-forests',
    title: 'Kelp Forests',
    tagline: 'Nearshore and offshore kelp — White Seabass, Yellowtail, Calico Bass',
    category: 'spearfishing',
    heroImage: heroWsbKelp,
    briefDescription:
      'Southern California\'s kelp forests are iconic spearfishing grounds. From nearshore beds along the coast to offshore kelp paddies drifting in blue water, these hunts target White Seabass, Yellowtail, Calico Bass, and seasonal Dorado.',
    fullDescription: [
      'Kelp forest spearfishing is the heart and soul of Southern California diving. The towering canopies create structure that attracts everything from Calico Bass and Sheephead to trophy White Seabass and cruising Yellowtail. Whether you are hunting the nearshore beds off Palos Verdes and the South Bay or running to offshore kelp paddies, every dive is different.',
      'Nearshore kelp trips are ideal for half-day charters and divers of all experience levels. The kelp beds close to shore hold reliable populations of Calico Bass, Sheephead, and Halibut, with White Seabass moving through during the late spring and early summer months.',
      'Offshore kelp paddies are a different game entirely. Floating mats of kelp in blue water attract Dorado, Yellowtail, and occasionally Bluefin Tuna. These are some of the most exciting dives in Southern California — you never know what will materialize out of the blue.',
    ],
    species: [
      { name: 'White Seabass', season: 'May - Jul' },
      { name: 'Yellowtail', season: 'Jul - Sept' },
      { name: 'Calico Bass', season: 'Year-round' },
      { name: 'Dorado (paddies)', season: 'Jul - Oct' },
      { name: 'Halibut', season: 'Apr - Sept' },
    ],
    tripOptions: [
      { name: 'Half-Day Charter', duration: '4 hours', price: 'From $1,799' },
      { name: 'Full-Day Charter', duration: '8 hours', price: 'From $2,999' },
    ],
    uniqueDetails: [
      'Nearshore beds and offshore paddies — two different experiences',
      'Half-day option makes this accessible for all schedules',
      'Great for developing spearfishing skills in a structured environment',
      'Seasonal Dorado on offshore kelp paddies',
    ],
    included: STANDARD_INCLUDED,
    toBring: STANDARD_TO_BRING,
    logistics: DEFAULT_LOGISTICS,
    seo: {
      title:
        'Kelp Forest Spearfishing Southern California — Charter | Fussaki',
      description:
        'Spearfishing in Southern California\'s iconic kelp forests. Nearshore and offshore kelp paddy hunting for White Seabass, Yellowtail, Calico Bass. Half-day and full-day trips from San Pedro.',
      h1: 'Kelp Forest Spearfishing Charters',
      keywords: [
        'kelp forest spearfishing',
        'spearfishing Southern California',
        'White Seabass kelp',
        'kelp paddy spearfishing',
        'nearshore spearfishing charter',
      ],
    },
  },

  // 5. Reefs & Structure
  {
    id: 'reefs',
    slug: 'reefs',
    title: 'Reefs & Structure',
    tagline: 'Reef systems and oil rig structures — year-round fish',
    category: 'spearfishing',
    heroImage: diverWsbUnderwater,
    briefDescription:
      'Southern California\'s reef systems and oil rig structures hold fish year-round. These trips target Calico Bass, Sheephead, Lingcod, and Halibut on rocky reef, while oil rigs offer vertical structure loaded with life. Great for beginners and experienced divers alike.',
    fullDescription: [
      'Reef and structure diving is the foundation of Southern California spearfishing. The rocky reefs along the coast and the network of oil platforms offshore provide consistent fish-holding habitat 365 days a year. These are reliable, productive trips that put fish on the stringer regardless of season.',
      'Calico Bass and Sheephead are year-round residents of the reef systems, and both are outstanding table fare. Lingcod hold on deeper structure, especially during the cooler months. Halibut patrol the sandy bottoms adjacent to reef edges from spring through fall, and when Yellowtail push through in summer, the reefs light up.',
      'Oil rig diving is a unique experience. The vertical structure of the platforms creates an artificial reef ecosystem that concentrates baitfish and predators alike. Calico Bass stack on the crossbeams, Sheephead cruise the pilings, and pelagics like Yellowtail and Barracuda make regular appearances.',
    ],
    species: [
      { name: 'Calico Bass', season: 'Year-round' },
      { name: 'Sheephead', season: 'Year-round' },
      { name: 'Lingcod', season: 'Year-round' },
      { name: 'Halibut', season: 'Apr - Sept' },
      { name: 'Yellowtail', season: 'Jul - Sept' },
    ],
    tripOptions: [
      { name: 'Half-Day Charter', duration: '4 hours', price: 'From $1,799' },
      { name: 'Full-Day Charter', duration: '8 hours', price: 'From $2,999' },
    ],
    uniqueDetails: [
      'Year-round fishery — no off-season',
      'Oil rig structure diving is a unique SoCal experience',
      'Half-day option for quick, productive sessions',
      'Great entry point for newer spearfishers',
    ],
    included: STANDARD_INCLUDED,
    toBring: STANDARD_TO_BRING,
    logistics: DEFAULT_LOGISTICS,
    seo: {
      title:
        'Reef & Oil Rig Spearfishing Los Angeles — Charter | Fussaki',
      description:
        'Spearfishing Southern California\'s reef systems and oil rig structures. Year-round fish, great for beginners. Calico Bass, Sheephead, Lingcod. Half and full-day trips from San Pedro.',
      h1: 'Reef & Structure Spearfishing Charters',
      keywords: [
        'reef spearfishing California',
        'oil rig spearfishing',
        'spearfishing Los Angeles',
        'Calico Bass spearfishing',
        'Sheephead spearfishing',
      ],
    },
  },

  // 6. Sportfishing
  {
    id: 'sportfishing',
    slug: 'sportfishing',
    title: 'Sportfishing',
    tagline: 'Rod and reel — Bluefin Tuna, Yellowtail, Dorado',
    category: 'sportfishing',
    heroImage: heroBluefinDeck,
    briefDescription:
      'Rod-and-reel sportfishing charters from San Pedro aboard the WorldCat 295CC. Captain Christos puts you on Bluefin Tuna, Yellowtail, Dorado, and Calico Bass with all tackle provided. Half-day and full-day options available.',
    fullDescription: [
      'Not every trip needs a speargun. Fussaki runs rod-and-reel sportfishing charters for anglers who want to hook into Southern California\'s best game fish from the deck of a dedicated fishing vessel. The WorldCat 295CC is rigged for both conventional and stand-up tackle, with live bait tanks and all the gear you need.',
      'Bluefin Tuna are the headline target from August through November when they push into Southern California waters. Yellowtail run from June to November and are one of the hardest-fighting fish pound-for-pound in the Pacific. Dorado show up on offshore kelp paddies from July to October, and Calico Bass are a reliable year-round option close to home.',
      'Captain Christos brings the same knowledge and intensity to sportfishing that he brings to spearfishing. Whether you are an experienced angler chasing a personal best or a group looking for a fun day on the water, every trip is tailored to what is biting and where.',
    ],
    species: [
      { name: 'Bluefin Tuna', season: 'Aug - Nov' },
      { name: 'Yellowtail', season: 'Jun - Nov' },
      { name: 'Dorado', season: 'Jul - Oct' },
      { name: 'Calico Bass', season: 'Year-round' },
      { name: 'White Seabass', season: 'May - Jul' },
    ],
    tripOptions: [
      { name: 'Half-Day Charter', duration: '4 hours', price: 'From $1,799' },
      { name: 'Full-Day Charter', duration: '8 hours', price: 'From $2,999' },
    ],
    uniqueDetails: [
      'All tackle and live bait provided',
      'WorldCat 295CC rigged for conventional and stand-up fishing',
      'Captain with deep knowledge of SoCal fishing grounds',
      'Great for groups, families, and corporate outings',
    ],
    included: [
      'Captain and crew',
      'All fishing tackle (rods, reels, terminal tackle)',
      'Live bait',
      'Safety equipment',
      'Fish handling — bled, iced, cleaned',
      'Ice and fish bags',
      'Fuel',
    ],
    toBring: STANDARD_TO_BRING,
    logistics: DEFAULT_LOGISTICS,
    seo: {
      title: 'Sportfishing Charters San Pedro CA — Rod & Reel | Fussaki',
      description:
        'Rod-and-reel sportfishing charters from San Pedro, CA. Target Bluefin Tuna, Yellowtail, Dorado, Calico Bass with Captain Christos. Tackle provided. Book your trip.',
      h1: 'Sportfishing Charters from San Pedro',
      keywords: [
        'sportfishing charter San Pedro',
        'fishing charter Los Angeles',
        'Bluefin Tuna fishing charter',
        'Yellowtail fishing charter',
        'rod and reel charter California',
      ],
    },
  },

  // 7. Private Charters
  {
    id: 'private',
    slug: 'private',
    title: 'Private Charters',
    tagline: 'Custom itinerary, your crew, your targets',
    category: 'private',
    heroImage: groupWsbCatch,
    briefDescription:
      'Book a fully private spearfishing or sportfishing charter with a custom itinerary. Perfect for corporate events, birthdays, bachelor parties, or a dedicated crew that wants the boat to themselves. Contact Captain Christos to build your trip.',
    fullDescription: [
      'Private charters give you the full Fussaki experience on your terms. Choose your target species, pick your destination, and set the schedule that works for your group. Whether it is a corporate team-building day, a birthday trip, or a crew of experienced divers with a specific mission, the trip is built around you.',
      'The WorldCat 295CC accommodates up to 6 guests and is equipped for both spearfishing and sportfishing. Captain Christos will work with you to plan the ideal itinerary based on your goals, experience level, and what the ocean is producing at the time.',
      'Private charters are available for any destination Fussaki runs — Catalina Island, San Clemente Island, kelp forests, reefs, blue water, or a combination. Mix spearfishing and rod-and-reel fishing in the same trip. The day is yours.',
    ],
    species: [
      { name: 'White Seabass', season: 'May - Jul' },
      { name: 'Yellowtail', season: 'Jun - Nov' },
      { name: 'Bluefin Tuna', season: 'Aug - Nov' },
      { name: 'Calico Bass', season: 'Year-round' },
      { name: 'Dorado', season: 'Jul - Oct' },
    ],
    tripOptions: [
      {
        name: 'Private Charter',
        duration: 'Custom',
        price: 'Contact for pricing',
      },
    ],
    uniqueDetails: [
      'Fully custom itinerary built around your goals',
      'Mix spearfishing and sportfishing in one trip',
      'Ideal for corporate events, birthdays, and group trips',
      'Available for any destination Fussaki runs',
    ],
    included: STANDARD_INCLUDED,
    toBring: STANDARD_TO_BRING,
    logistics: DEFAULT_LOGISTICS,
    seo: {
      title: 'Private Fishing Charter San Pedro — Custom Trips | Fussaki',
      description:
        'Book a private spearfishing or sportfishing charter from San Pedro. Custom itinerary, your crew, your targets. WorldCat 295CC. Corporate events, birthdays, group trips.',
      h1: 'Private Fishing Charters',
      keywords: [
        'private fishing charter San Pedro',
        'custom fishing charter Los Angeles',
        'private spearfishing charter',
        'corporate fishing charter',
        'group fishing charter California',
      ],
    },
  },
];

/**
 * Look up a single charter destination by its URL slug.
 */
export function getCharterBySlug(
  slug: string,
): CharterDestination | undefined {
  return charterDestinations.find((d) => d.slug === slug);
}

/**
 * Return all destinations belonging to a given category.
 */
export function getChartersByCategory(
  category: CharterDestination['category'],
): CharterDestination[] {
  return charterDestinations.filter((d) => d.category === category);
}
